import { action, flow, makeObservable, observable } from 'mobx';
import { Property } from '@roc/feature-types';
import { FormStore } from '@roc/feature-app-core';
import { GlobalStore } from '@roc/feature-app-core';
import { getUniqueId, parseAddress, MIXED_USE, MULTIFAMILY_5_PLUS, generateUUID } from '@roc/feature-utils';

const propertyInformationForm = {
  fields: {
    address: {
      value: '',
      error: null,
      rule: 'required',
    },
    __isAddressInCorrectFormat__: {
      value: true,
      error: null,
      rule: 'accepted',
      message: 'Invalid Address Format',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: 'required',
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required',
    },
    latitude: {
      value: 0,
      error: null,
      rule: '',
    },
    longitude: {
      value: 0,
      error: null,
      rule: '',
    },
    useCode: {
      value: '',
      error: null,
      rule: 'required',
    },
    aptNumber: {
      value: '',
      error: null,
      rule: '',
    },
    propertyOwnership: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const mixedUsePropertyInformationForm = {
  fields: {
    residentialUnits: {
      value: '',
      error: null,
      rule: 'required',
    },
    commercialUnits: {
      value: '',
      error: null,
      rule: 'required',
    },
    residentialUnitsSqFtArea: {
      value: '',
      error: null,
      rule: 'required',
    },
    commercialUnitsSqFtArea: {
      value: '',
      error: null,
      rule: 'required',
    },
    residentialIncome: {
      value: '',
      error: null,
      rule: 'required',
    },
    commercialIncome: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const DSCRPropertyInformationForm = {
  fields: {
    monthlyGrossRent: {
      value: '',
      error: null,
      rule: 'required',
    },
    annualTaxes: {
      value: '',
      error: null,
      rule: 'required',
    },
    annualInsurance: {
      value: '',
      error: null,
      rule: 'required',
    },
    annualHOA: {
      value: '',
      error: null,
      rule: 'required',
    },
    totalAnnualUtilities: {
      value: '',
      error: null,
      rule: 'required',
    },
    totalAnnualRepairsMaintenance: {
      value: '',
      error: null,
      rule: 'required',
    },
    totalAnnualPropertyManagementFees: {
      value: '',
      error: null,
      rule: 'required',
    },
    generalAdministrative: {
      value: '',
      error: null,
      rule: 'required',
    },
    payroll: {
      value: '',
      error: null,
      rule: 'required',
    },
    marketing: {
      value: '',
      error: null,
      rule: 'required',
    },
    replacementReserves: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const multiFamilyPropertyInformationForm = {
  fields: {
    units: {
      value: '',
      error: null,
      rule: 'required',
    },
    currentOccupancy: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const purchaseInformationForm = {
  fields: {
    purchasePrice: {
      value: '',
      error: null,
      rule: 'required|max:1000000000000',
    },
    armsLength: {
      value: '',
      error: null,
      rule: 'required',
    },
    armsLengthComment: {
      value: '',
      error: null,
      rule: [{ required_if: ['armsLength', 'N'] }],
    },
    propertySourcing: {
      value: '',
      error: null,
      rule: 'required',
    },
    propertySourcingExplanation: {
      value: '',
      error: null,
      rule: [
        { required_if: ['propertySourcing', 'Other'] },
        { required_if: ['wholesalerPurchase', 'Other'] },
      ],
    },
    wholesaleAmount: {
      value: '',
      error: null,
      rule: [
        { required_if: ['propertySourcing', 'Wholesaler'] },
        { max: 1000000000000 },
      ],
    },
    wholesalerPurchase: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertySourcing', 'Wholesaler'] }],
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const refinance_purchaseInformationForm = {
  fields: {
    anyDebt: {
      value: '',
      error: null,
      rule: 'required',
    },
    refiAmount: {
      value: '',
      error: null,
      rule: '',
    },
    purchaseDate: {
      value: null,
      error: null,
      rule: 'required',
    },
    purchasePrice: {
      value: '',
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const renovationDetailsForm = {
  fields: {
    totalRenovationBudget: {
      value: '',
      error: null,
      rule: 'required|max:1000000000000',
    },
    rehabType: {
      value: '',
      error: null,
      rule: '',
    },
    describeRenovation: {
      value: '',
      error: null,
      rule: [{ required_unless: ['totalRenovationBudget', 0] }],
    },
    sunkCost: {
      value: 0,
      error: null,
      rule: '',
    },
    renovationRemaining: {
      value: 0,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const refinance_renovationDetailsForm = {
  fields: {
    totalRenovationBudget: {
      value: '',
      error: null,
      rule: 'required|max:1000000000000',
    },
    sunkCost: {
      value: 0,
      error: null,
      rule: 'required|max:1000000000000',
    },
    renovationBudget: {
      value: '',
      error: null,
      rule: '',
    },
    renovationRemaining: {
      value: '',
      error: null,
      rule: '',
    },
    describeRenovation: {
      value: '',
      error: null,
      rule: [{ required_unless: ['renovationRemaining', 0] }],
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const otherDetailsForm = {
  fields: {
    exitStrategy: {
      value: '',
      error: null,
      rule: 'required',
    },
    asIsValue: {
      value: '',
      error: null,
      rule: 'required|max:1000000000000',
    },
    additionalComments: {
      value: '',
      error: null,
      rule: '',
    },
    redFlagComments: {
      value: '',
      error: null,
      rule: '',
    },
    submitRushedAppraisal: {
      value: false,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};
const afterRepairValueForm = {
  fields: {
    afterRepairValue: {
      value: '',
      error: null,
      rule: 'required',
    }
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const refinance_otherDetailsForm = {
  fields: {
    asIsValue: {
      value: '',
      error: null,
      rule: 'required|max:1000000000000',
    },
    exitStrategy: {
      value: '',
      error: null,
      rule: 'required',
    },
    additionalComments: {
      value: '',
      error: null,
      rule: '',
    },
    redFlagComments: {
      value: '',
      error: null,
      rule: '',
    },
    submitRushedAppraisal: {
      value: false,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const appraisalInformationForm = {
  fields: {
    chooseExistingAppraisal: {
      value: true,
      error: null,
      rule: 'required',
    },
    orderId: {
      value: null,
      error: null,
      rule: [{ required_if: ['chooseExistingAppraisal', true] }],
      message: 'This field is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

class PropertyInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...propertyInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }

  private saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );
    this.onFieldChange('address', address);
    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('latitude', parsedAddress.latitude);
    this.onFieldChange('longitude', parsedAddress.longitude);

    const addressInCorrectFormat = parsedAddress.street_number !== null && parsedAddress.street !== null && parsedAddress.city !== null && parsedAddress.state !== null && parsedAddress.zip !== null;
    this.onFieldChange('__isAddressInCorrectFormat__', addressInCorrectFormat);
  }
}
class MixedUsePropertyInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...mixedUsePropertyInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class MultiFamilyPropertyInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...multiFamilyPropertyInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}
class DSCRPropertyInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...DSCRPropertyInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}
class AfterRepairValueStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...afterRepairValueForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}
class AfterRepairValueRefStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...afterRepairValueForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class PurchaseInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...purchaseInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class Refinance_PurchaseInformationStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...refinance_purchaseInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class RenovationDetailsStore extends FormStore {
  private globalStore: GlobalStore;
  private purchaseInformationStore: PurchaseInformationStore;
  private onRenovationBudgetChanged: (value: number) => void;
  constructor(
    globalStore,
    purchaseInformationStore,
    onRenovationBudgetChanged?: (value: number) => void
  ) {
    super({ ...renovationDetailsForm }, globalStore);
    this.globalStore = globalStore;
    this.purchaseInformationStore = purchaseInformationStore;
    this.onRenovationBudgetChanged = onRenovationBudgetChanged;
    makeObservable(this, {
      onFormSubmit: flow,
      handleRenovationBudgetChange: action,
    });
  }

  handleRenovationBudgetChange() {
    const { totalRenovationBudget } = this.getFormValues();
    const { purchasePrice } = this.purchaseInformationStore.getFormValues();
    if (!totalRenovationBudget) {
      this.onFieldChange('describeRenovation', '');
    }
    this.onFieldChange('renovationRemaining', totalRenovationBudget);

    if (!this.onRenovationBudgetChanged) return;
    this.onRenovationBudgetChanged(totalRenovationBudget);
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class Refinance_RenovationDetailsStore extends FormStore {
  private globalStore: GlobalStore;
  private afterRepairValueStore: AfterRepairValueStore;
  private refinanceOtherDetailsStore: Refinance_OtherDetailsStore;
  constructor(globalStore, refinanceOtherDetailsStore, afterRepairValueStore) {
    super({ ...refinance_renovationDetailsForm }, globalStore);
    this.globalStore = globalStore;
    this.afterRepairValueStore = afterRepairValueStore;
    this.refinanceOtherDetailsStore = refinanceOtherDetailsStore;
    makeObservable(this, {
      onFormSubmit: flow,
      handleRenovationBudgetChange: action,
      handleRenovationSpentToDateChange: action,
    });
  }

  setFormFieldValue(field, value) {
    this.form.fields[field].value = value;
    this.form.fields[field].error = null;
  }

  handleRenovationBudgetChange() {
    const { sunkCost, totalRenovationBudget } = this.getFormValues();
    if (!sunkCost || totalRenovationBudget >= sunkCost) {
      if (totalRenovationBudget <= 1000000000000) {
        if (!totalRenovationBudget) {
          this.afterRepairValueStore.onFieldChange('afterRepairValue', '');
        }
        const renovationRemaining = totalRenovationBudget - sunkCost;
        if (!renovationRemaining) {
          this.onFieldChange('describeRenovation', '');
          this.afterRepairValueStore.onFieldChange('afterRepairValue', '');
          this.refinanceOtherDetailsStore.onFieldChange('exitStrategy', '');
        }
        this.onFieldChange('renovationRemaining', renovationRemaining);
      }
    } else {
      this.globalStore.notificationStore.showWarningNotification({
        message:
          'The total renovation budget should be greater than the amount already invested.',
      });
    }
  }

  handleRenovationSpentToDateChange() {
    const { sunkCost, totalRenovationBudget } = this.getFormValues();
    if (!totalRenovationBudget || sunkCost <= totalRenovationBudget) {
      const renovationRemaining = totalRenovationBudget - sunkCost;
      if (sunkCost <= 1000000000000) {
        this.onFieldChange('renovationRemaining', renovationRemaining);
      }
      if (!renovationRemaining) {
        this.onFieldChange('describeRenovation', '');
        this.afterRepairValueStore.onFieldChange('afterRepairValue', '');
        this.refinanceOtherDetailsStore.onFieldChange('exitStrategy', '');
      }
    } else {
      this.globalStore.notificationStore.showWarningNotification({
        message:
          'Amount already invested should be lower than the total renovation budget.',
      });
    }
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class OtherDetailsStore extends FormStore {
  private globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...otherDetailsForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class Refinance_OtherDetailsStore extends FormStore {
  public globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...refinance_otherDetailsForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

class AppraisalInformationStore extends FormStore {
  public globalStore: GlobalStore;
  constructor(globalStore) {
    super({ ...appraisalInformationForm }, globalStore);
    this.globalStore = globalStore;
    makeObservable(this, {
      onFormSubmit: flow,
    });
  }

  *onFormSubmit() {
    this.form.meta.error = '';
  }
}

export class PropertyStore {
  private globalStore: GlobalStore;
  propertyId: string;
  appraisalId: string;
  loanApplicationUuid: string;
  propertyInformationStore: PropertyInformationStore;
  mixedUsePropertyInformationStore: MixedUsePropertyInformationStore;
  multiFamilyPropertyInformationStore: MultiFamilyPropertyInformationStore;
  dscrPropertyInformationStore: DSCRPropertyInformationStore;
  afterRepairValueStore: AfterRepairValueStore;
  afterRepairValueRefStore: AfterRepairValueRefStore;
  purchaseInformationStore: PurchaseInformationStore;
  renovationDetailsStore: RenovationDetailsStore;
  otherDetailsStore: OtherDetailsStore;
  refinance_purchaseInformationStore: Refinance_PurchaseInformationStore;
  refinance_renovationDetailsStore: Refinance_RenovationDetailsStore;
  refinance_otherDetailsStore: Refinance_OtherDetailsStore;
  appraisalInformationStore: AppraisalInformationStore;

  constructor(
    globalStore,
    onRenovationBudgetChanged?: (value: number) => void
  ) {
    this.globalStore = globalStore;
    this.propertyInformationStore = new PropertyInformationStore(
      this.globalStore
    );
    this.mixedUsePropertyInformationStore = new MixedUsePropertyInformationStore(
      this.globalStore
    );
    this.multiFamilyPropertyInformationStore = new MultiFamilyPropertyInformationStore(
      this.globalStore
    );
    this.dscrPropertyInformationStore = new DSCRPropertyInformationStore(
      this.globalStore
    );
    this.afterRepairValueStore = new AfterRepairValueStore(
      this.globalStore
    );
    this.afterRepairValueRefStore = new AfterRepairValueRefStore(
      this.globalStore
    );
    this.purchaseInformationStore = new PurchaseInformationStore(
      this.globalStore
    );
    this.renovationDetailsStore = new RenovationDetailsStore(
      this.globalStore,
      this.purchaseInformationStore,
      onRenovationBudgetChanged
    );
    this.otherDetailsStore = new OtherDetailsStore(this.globalStore);
    this.refinance_purchaseInformationStore = new Refinance_PurchaseInformationStore(
      this.globalStore
    );
    this.refinance_otherDetailsStore = new Refinance_OtherDetailsStore(
      this.globalStore
    );
    this.refinance_renovationDetailsStore = new Refinance_RenovationDetailsStore(
      this.globalStore,
      this.refinance_otherDetailsStore,
      this.afterRepairValueStore
    );
    this.appraisalInformationStore = new AppraisalInformationStore(
      this.globalStore
    );
    makeObservable(this, {
      propertyId: observable,
      appraisalId: observable,
      setPropertyId: action,
      setLoanApplicationUuid: action,
      reset: action,
      loadProperty: action,
      loadPropertyFromAppraisal: action,
      getProperty: action,
      setAppraisalId: action,
    });
  }

  loadProperty(property: Property, runValidations: boolean = true) {
    this.setAppraisalId(property.appraisalId);
    this.setPropertyId(property.propertyId);
    this.setLoanApplicationUuid(property.loanApplicationUuid);

    this.propertyInformationStore.loadForm(property);
    this.mixedUsePropertyInformationStore.loadForm(property);
    this.multiFamilyPropertyInformationStore.loadForm(property);
    this.dscrPropertyInformationStore.loadForm(property);
    this.afterRepairValueStore.loadForm(property);
    this.afterRepairValueRefStore.loadForm(property);
    this.purchaseInformationStore.loadForm(property);
    this.renovationDetailsStore.loadForm(property);
    this.otherDetailsStore.loadForm(property);
    this.refinance_purchaseInformationStore.loadForm(property);
    this.refinance_renovationDetailsStore.loadForm(property);
    this.refinance_otherDetailsStore.loadForm(property);

    if (runValidations) {
      this.runAllFormValidations();
    }
  }

  loadPropertyFromAppraisal(appraisal) {
    const appraisalProperty = JSON.parse(
      appraisal?.appraisalData?.propertyAppraisalOrderJson
    );
    const property: Property = {
      propertyId: appraisal?.appraisalData?.propertyId,
      appraisalId: appraisal?.appraisalData?.propertyAppraisalId,
      address: appraisalProperty?.property?.address,
      streetNumber: appraisalProperty?.property?.streetNumber,
      streetName: appraisalProperty?.property?.streetName,
      city: appraisalProperty?.property?.city,
      state: appraisalProperty?.property?.state,
      zipCode: appraisalProperty?.property?.zipCode,
      latitude: appraisalProperty?.property?.latitude,
      longitude: appraisalProperty?.property?.longitude,
      useCode: appraisalProperty?.property?.propertyType,
      aptNumber: appraisalProperty?.property?.aptNumber,
      propertyOwnership: appraisalProperty?.propertyOwnership,
      purchasePrice: appraisalProperty?.purchasePrice,
      purchaseDate: appraisalProperty?.purchaseDate,
      armsLength: appraisalProperty?.armsLength,
      armsLengthComment: appraisalProperty?.armsLengthComment,
      propertySourcing: appraisalProperty?.propertySourcing,
      propertySourcingExplanation:
        appraisalProperty?.property?.propertySourcingExplanation,
      wholesaleAmount: appraisalProperty?.wholesaleAmount,
      wholesalerPurchase: appraisalProperty?.wholesalerPurchase,
      anyDebt: appraisalProperty?.property?.anyDebt,
      //refiAmount:
      totalRenovationBudget: appraisalProperty?.renovationBudget,
      describeRenovation: appraisalProperty?.describeRenovation,
      sunkCost: appraisalProperty?.sunkCost,
      afterRepairValue: appraisalProperty?.afterRepairValue,
      exitStrategy: appraisalProperty?.exitStrategy,
      additionalComments: appraisalProperty?.property?.additionalComments,
      submitRushedAppraisal: appraisalProperty?.property?.submitRushedAppraisal,
    };
    this.loadProperty(property, false);
  }

  runAllFormValidations() {
    this.propertyInformationStore.runFormValidation();
    this.purchaseInformationStore.runFormValidation();
    this.renovationDetailsStore.runFormValidation();
    this.otherDetailsStore.runFormValidation();
    this.refinance_purchaseInformationStore.runFormValidation();
    this.refinance_renovationDetailsStore.runFormValidation();
    this.refinance_otherDetailsStore.runFormValidation();
    this.multiFamilyPropertyInformationStore.runFormValidation();
    if (this.renovationDetailsStore.form.fields.totalRenovationBudget.value > 0) {
      this.afterRepairValueStore.runFormValidation();
    }
    if (
      this.refinance_renovationDetailsStore.form.fields.totalRenovationBudget.value > 0 ||
      this.refinance_renovationDetailsStore.form.fields.renovationBudget.value > 0) {
      this.afterRepairValueRefStore.runFormValidation();
    }
  }

  setPropertyId(propertyId) {
    this.propertyId = propertyId;
  }

  setAppraisalId(appraisalId) {
    this.appraisalId = appraisalId;
  }

  setLoanApplicationUuid(uuid){
    this.loanApplicationUuid = uuid;
  }

  getProperty() {
    if (
      this.propertyInformationStore.form.fields.propertyOwnership.value ==
      'Purchase'
    ) {
      let property = {
        propertyId: this.propertyId ?? getUniqueId(),
        appraisalId: this.appraisalId,
        loanApplicationUuid: this.loanApplicationUuid ?? generateUUID(),
        ...this.propertyInformationStore.getFormValues(),
        ...this.purchaseInformationStore.getFormValues(),
        ...this.renovationDetailsStore.getFormValues(),
        ...this.otherDetailsStore.getFormValues(),
      };
      if (this.propertyInformationStore.form.fields.useCode.value === MIXED_USE) {
        property = { ...property, ...this.mixedUsePropertyInformationStore.getFormValues() }
      }
      if (this.propertyInformationStore.form.fields.useCode.value === MULTIFAMILY_5_PLUS) {
        property = { ...property, ...this.multiFamilyPropertyInformationStore.getFormValues() }
      }
      if (this.renovationDetailsStore.form.fields.totalRenovationBudget.value > 0) {
        property = { ...property, ...this.afterRepairValueStore.getFormValues() }
      }
      return property;
    } else {
      let property = {
        propertyId: this.propertyId ?? getUniqueId(),
        appraisalId: this.appraisalId,
        loanApplicationUuid: this.loanApplicationUuid ?? generateUUID(),
        ...this.propertyInformationStore.getFormValues(),
        ...this.refinance_purchaseInformationStore.getFormValues(),
        ...this.refinance_renovationDetailsStore.getFormValues(),
        ...this.refinance_otherDetailsStore.getFormValues(),
        ...this.appraisalInformationStore.getFormValues(),
      }
      this.propertyInformationStore.form.fields.useCode.value = MULTIFAMILY_5_PLUS;
      property = {
        ...property, ...this.multiFamilyPropertyInformationStore.getFormValues()}
      if (this.refinance_renovationDetailsStore.form.fields.totalRenovationBudget.value > 0 ||
        this.refinance_renovationDetailsStore.form.fields.renovationBudget.value > 0) {
        property = { ...property, ...this.afterRepairValueRefStore.getFormValues() }
      }
      return property;
    }
  }

  reset() {
    this.propertyInformationStore.reset();
    this.mixedUsePropertyInformationStore.reset();
    this.multiFamilyPropertyInformationStore.reset();
    this.dscrPropertyInformationStore.reset();
    this.afterRepairValueStore.reset();
    this.afterRepairValueRefStore.reset();
    this.purchaseInformationStore.reset();
    this.renovationDetailsStore.reset();
    this.otherDetailsStore.reset();
    this.refinance_purchaseInformationStore.reset();
    this.refinance_renovationDetailsStore.reset();
    this.refinance_otherDetailsStore.reset();
    this.appraisalInformationStore.reset();
    this.appraisalId = null;
    this.propertyId = null;
  }
}

export default PropertyStore;
